import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { fetchMyProfile, selectMyProfile, selectProfileStatus } from '../../store/slices/profileSlice';
import { PageLoader } from '../ui/PageLoader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const SETUP_EXEMPT = ['/profile/setup', '/profile/edit', '/verify', '/support', '/settings', '/subscription'];

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, initialized } = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectMyProfile);
  const profileStatus = useAppSelector(selectProfileStatus);

  useEffect(() => {
    if (isAuthenticated && profileStatus === 'idle') {
      dispatch(fetchMyProfile());
    }
  }, [isAuthenticated, profileStatus, dispatch]);

  if (!initialized) return <PageLoader />;
  if (!isAuthenticated) return <Navigate to="/" state={{ from: location }} replace />;

  const onExemptRoute = SETUP_EXEMPT.some((p) => location.pathname.startsWith(p));

  // Wait for the profile fetch to complete before deciding whether to redirect.
  if (!onExemptRoute && (profileStatus === 'idle' || profileStatus === 'loading')) {
    return <PageLoader />;
  }

  // Redirect to setup if the user hasn't finished profile setup yet.
  if (!onExemptRoute && !profile?.isSetupComplete) {
    return <Navigate to="/profile/setup" replace />;
  }

  return <>{children}</>;
}
